import * as React from 'react';
import { Maximize2, Minimize2, Monitor, Moon, Sun } from 'lucide-react';
import { Button } from '../ui/primitives';
import { useThemeStore, type ThemePreference } from './theme.store';
import { t } from '../../i18n';

/**
 * Keeps the store in step with the OS while "system" is selected, and applies
 * the stored preference once on mount. Render once, high in the tree.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const preference = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);
  const syncWithSystem = useThemeStore((s) => s.syncWithSystem);

  React.useEffect(() => {
    setPreference(preference);
    // Only on mount: afterwards the store applies the theme itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => syncWithSystem();
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [syncWithSystem]);

  return <>{children}</>;
}

const LABEL: Record<ThemePreference, string> = {
  light: 'Light theme',
  dark: 'Dark theme',
  system: 'Match system theme',
};

const NEXT: Record<ThemePreference, ThemePreference> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

/**
 * One button that cycles light → dark → system.
 *
 * The accessible name says both where you are and where the next press goes,
 * because an icon-only toggle otherwise tells a screen-reader user nothing
 * about what pressing it will do.
 */
export function ThemeToggle(): React.JSX.Element {
  const preference = useThemeStore((s) => s.preference);
  const cycle = useThemeStore((s) => s.cycle);

  const Icon = preference === 'light' ? Sun : preference === 'dark' ? Moon : Monitor;

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={cycle}
      aria-label={t('{0}. Switch to {1}.', [LABEL[preference], LABEL[NEXT[preference]].toLowerCase()])}
      title={LABEL[preference]}
    >
      <Icon className="size-[18px]" aria-hidden />
    </Button>
  );
}

/**
 * Full-screen toggle.
 *
 * Worth having on a console whose main job is dense tables — a bill run on a
 * 13" laptop gains four rows without the browser chrome. Hidden where the API
 * is unavailable (iOS Safari) rather than rendering a button that does nothing.
 */
export function FullscreenToggle(): React.JSX.Element | null {
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [supported, setSupported] = React.useState(false);

  React.useEffect(() => {
    setSupported(document.fullscreenEnabled);
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  if (!supported) return null;

  const toggle = () => {
    // Both calls reject if the gesture is not trusted; a rejected promise here
    // is not worth an error toast.
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void document.documentElement.requestFullscreen().catch(() => undefined);
    }
  };

  const Icon = isFullscreen ? Minimize2 : Maximize2;

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label={isFullscreen ? t('Exit full screen') : t('Enter full screen')}
      aria-pressed={isFullscreen}
      title={isFullscreen ? t('Exit full screen') : t('Full screen')}
    >
      <Icon className="size-[18px]" aria-hidden />
    </Button>
  );
}
